type DatedRow = {
  date: string
}

const toTimestamp = (value: string): number | null => {
  const parsed = Date.parse(String(value ?? ''))
  return Number.isFinite(parsed) ? parsed : null
}

export const selectLatestDatedRow = <T extends DatedRow>(rows: ReadonlyArray<T>): T | null => {
  if (rows.length === 0) return null

  return rows.reduce<T | null>((best, row) => {
    if (!best) return row
    return String(row.date ?? '').localeCompare(String(best.date ?? '')) > 0 ? row : best
  }, null)
}

export const selectNearestDatedRow = <T extends DatedRow>(targetDate: string, rows: ReadonlyArray<T>): T | null => {
  if (rows.length === 0) return null

  const targetTimestamp = toTimestamp(targetDate)
  if (targetTimestamp === null) {
    return selectLatestDatedRow(rows)
  }

  return rows.reduce<T | null>((best, row) => {
    const rowTimestamp = toTimestamp(row.date)
    if (rowTimestamp === null) return best
    if (!best) return row

    const bestTimestamp = toTimestamp(best.date)
    if (bestTimestamp === null) return row

    const rowDistance = Math.abs(rowTimestamp - targetTimestamp)
    const bestDistance = Math.abs(bestTimestamp - targetTimestamp)

    if (rowDistance !== bestDistance) {
      return rowDistance < bestDistance ? row : best
    }

    return rowTimestamp > bestTimestamp ? row : best
  }, null) ?? selectLatestDatedRow(rows)
}

export const buildLatestDatedRowByKey = <T extends DatedRow>(
  rows: ReadonlyArray<T>,
  resolveKey: (row: T) => string | null | undefined
): Map<string, T> => {
  const latestByKey = new Map<string, T>()

  for (const row of rows) {
    const key = resolveKey(row)
    if (!key) continue

    const current = latestByKey.get(key)
    if (!current) {
      latestByKey.set(key, row)
      continue
    }

    const latest = selectLatestDatedRow([current, row])
    if (latest) {
      latestByKey.set(key, latest)
    }
  }

  return latestByKey
}

export const buildLatestDatedRowByKeyInWindow = <T extends DatedRow>(
  rows: ReadonlyArray<T>,
  resolveKey: (row: T) => string | null | undefined,
  start: Date,
  end: Date
): Map<string, T> => {
  const startTime = start.getTime()
  const endTime = end.getTime()

  return buildLatestDatedRowByKey(rows.filter((row) => {
    const rowTime = toTimestamp(row.date)
    if (rowTime === null) return false
    return rowTime >= startTime && rowTime < endTime
  }), resolveKey)
}